import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useState, useEffect, useRef } from 'react';
import styles from './Image.module.css';
export const Image = forwardRef(({ src, alt, fallbackSrc = '/placeholder.jpg', aspectRatio = 'auto', objectFit = 'cover', lazy = true, blur = true, className = '', onLoad, onError, ...props }, ref) => {
    const [imageSrc, setImageSrc] = useState(lazy ? undefined : src);
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const imageRef = useRef(null);
    const combinedRef = ref || imageRef;
    useEffect(() => {
        if (!lazy || !('IntersectionObserver' in window)) {
            setImageSrc(src);
            return;
        }
        const imageElement = ref
            ? ref.current
            : imageRef.current;
        if (!imageElement)
            return;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setImageSrc(src);
                    observer.unobserve(entry.target);
                }
            });
        }, {
            rootMargin: '50px',
        });
        observer.observe(imageElement);
        return () => {
            observer.disconnect();
        };
    }, [src, lazy, ref]);
    const handleLoad = (e) => {
        setIsLoaded(true);
        setHasError(false);
        onLoad?.(e);
    };
    const handleError = (e) => {
        setHasError(true);
        if (fallbackSrc && imageSrc !== fallbackSrc) {
            setImageSrc(fallbackSrc);
        }
        onError?.(e);
    };
    const wrapperClasses = [
        styles.wrapper,
        styles[`ratio${aspectRatio.replace(':', '_')}`],
        className,
    ]
        .filter(Boolean)
        .join(' ');
    const imageClasses = [
        styles.image,
        blur && !isLoaded && styles.blur,
        styles[`fit${objectFit.charAt(0).toUpperCase() + objectFit.slice(1).replace('-', '')}`],
    ]
        .filter(Boolean)
        .join(' ');
    return (_jsxs("div", { className: wrapperClasses, children: [_jsx("img", { ref: combinedRef, src: imageSrc, alt: alt, className: imageClasses, onLoad: handleLoad, onError: handleError, ...props }), !isLoaded && !hasError && (_jsx("div", { className: styles.placeholder, "aria-hidden": "true" }))] }));
});
Image.displayName = 'Image';
